import { DateTime } from 'luxon'
import React from 'react'
import * as Content from '../Content'
import * as Avatar from '../Avatar'
import { Title } from './Title'
import { Body } from './Body'
import { View, CardViewProps } from './View'
import { truncate } from 'utils'

export interface InvitationCardProps extends CardViewProps {
  invite: any // Anything typed as 'any' WAS utilizing 'EventInvitation' from the epc schema
  truncate?: number
  showStatus?: boolean
}

const colorForTag = (status: string) => {
  switch (
    status // this is actually EventInvitationStatus from the epc schema.
  ) {
    case 'CANCELLED':
      return 'destructive'
    case 'DECLINED':
      return 'warning'
    case 'PENDING':
      return 'warning'
    default:
      return undefined
  }
}

export const InvitationAttendee: React.FC<InvitationCardProps> = ({
  invite,
  onClick: handleClick,
  showStatus,
  ...view
}) => {
  return (
    <View {...view}>
      <button className="flex flex-grow text-left" onClick={handleClick}>
        <div className="mr-2 flex-shrink-0">
          <Avatar.Simple
            color={invite.profileColor || ''}
            name={invite.profileName}
            maxInitials={2}
            imageUrl={invite.profileIconUrl || ''}
          />
        </div>
        <div className="flex flex-col my-auto">
          <Title>{invite.profileName}</Title>
          <Body>
            {invite.profileDescription}
            <br />
            {invite.profileDescription2}
          </Body>
          {showStatus ? (
            <p className="flex mt-1 mr-auto">
              <Content.Bubble bubbleStyle={colorForTag(invite.status)}>
                {invite.status.replace('_', ' ')}
              </Content.Bubble>
            </p>
          ) : null}
        </div>
      </button>
    </View>
  )
}

InvitationAttendee.displayName = 'Card.InvitationAttendee'

export const InvitationProfile: React.FC<InvitationCardProps> = ({
  invite,
  onClick: handleClick,
  showStatus,
  ...view
}) => {
  return (
    <View {...view}>
      <button className="flex flex-grow text-left" onClick={handleClick}>
        <div className="mr-2 flex-shrink-0">
          <Avatar.Simple
            color={invite.profileColor || ''}
            name={invite.profileName}
            maxInitials={2}
            imageUrl={invite.profileIconUrl || ''}
          />
        </div>
        <div className="flex flex-col my-auto">
          <Title>{invite.profileName}</Title>
          <Body>
            {invite.status} • Invited{' '}
            {DateTime.fromISO(invite.createdAt).toRelative()}
          </Body>
          {showStatus ? (
            <p className="flex mt-1 mr-auto">
              <Content.Bubble bubbleStyle={colorForTag(invite.status)}>
                {invite.status.replace('_', ' ')}
              </Content.Bubble>
            </p>
          ) : null}
        </div>
      </button>
    </View>
  )
}

InvitationProfile.displayName = 'Card.InvitationProfile'

export const InvitationEvent: React.FC<InvitationCardProps> = ({
  invite,
  onClick: handleClick,
  truncate: truncateLimit,
  showStatus,
  ...view
}) => {
  return (
    <View {...view}>
      <button className="flex flex-grow text-left" onClick={handleClick}>
        <div className="mr-2 flex-shrink-0">
          <Avatar.Simple
            color={invite.eventColor || ''}
            name={invite.eventName}
            maxInitials={1}
            imageUrl={invite.eventIconUrl || ''}
          />
        </div>
        <div className="flex flex-col my-auto">
          <Title className="truncate">
            {truncate(invite.eventName, truncateLimit ?? 155)}
          </Title>
          <Body>Invited {DateTime.fromISO(invite.createdAt).toRelative()}</Body>
          {showStatus ? (
            <p className="flex mt-1 mr-auto">
              <Content.Bubble bubbleStyle={colorForTag(invite.status)}>
                {invite.status.replace('_', ' ')}
              </Content.Bubble>
            </p>
          ) : null}
        </div>
      </button>
    </View>
  )
}

InvitationEvent.displayName = 'Card.InvitationEvent'
